import { useEffect, useState } from "react";
import { GoSearch } from "react-icons/go";
import { useSelector } from "react-redux";
import BackButton from "../../components/BackButton";
import SpinnerLoader from "../../components/SpinnerLoader";
import TabNavbar from "../../components/Tabbar";
import ErrorModal from "../../components/modal/ErrorModal";
import SaveConfirmModal from "../../components/modal/SaveConfirmModal";
import {
  SessionBranchItem,
  SessionGroupItem,
} from "../../interfaces/allocation";
import { SessionStatus } from "../../interfaces/sessions";
import {
  getCourseSessionBranches,
  getCourseSessionGroups,
  setSelectedCourseSessionData,
  updateSessionSeatAllocation,
} from "../../redux/slices/allocation.slice";
import {
  getDoceboSession,
  updateSessionById,
} from "../../redux/slices/session.slice";
import { RootState, useAppDispatch } from "../../redux/store";
import { ADMIN_TABS } from "../../utils/TabbarData";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../components/LanguageSelector";
import { IoMdSwap } from "react-icons/io";

function SeatAllocation() {
  const [selectedType, setSelectedType] = useState("branch");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [modalMessage, setModalMessage] = useState("");
  const [sessionStatus, setSessionStatus] = useState<SessionStatus | null>(
    null
  );
  const [minMaxEnroll, setMinMaxEnroll] = useState({
    min: 1,
    max: 1,
  });
  const [sessionBranches, setSessionBranches] = useState<SessionBranchItem[]>(
    []
  );
  const [errorMessage, setErrorMessage] = useState<any>(null);
  const [sessionGroups, setSessionGroups] = useState<SessionGroupItem[]>([]);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const selectedTypeClassName = "bg-white text-[#961e38]";

  const {
    branches,
    groups,
    branchLoading,
    groupLoading,
    selectedSessionData,
    saveLoading,
  } = useSelector((state: RootState) => state.allocation);
  const { selectedCourseData, loading }: any = useSelector(
    (state: RootState) => state.sessions
  );
  const { doceboSessionsData } = useSelector(
    (state: RootState) => state.sessions
  );

  useEffect(() => {
    if (doceboSessionsData) {
      setMinMaxEnroll((prev) => ({
        ...prev,
        min: doceboSessionsData.min_enroll,
      }));
    }
  }, [doceboSessionsData]);

  const isEditableStatus = (status: SessionStatus | null) =>
    ["ALLOCATION", "APPLICATION", "EXCLUDED_AT_ALLOC"].includes(status || "");

  const filteredBranches = sessionBranches.filter((branch) =>
    branch.branch_title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredGroups = sessionGroups.filter((group) =>
    group.group_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const validateSeatAllocation = () => {
    const totalBranchSeats = sessionBranches.reduce(
      (sum, branch) => sum + branch.seat_allocated,
      0
    );
    const totalGroupSeats = sessionGroups.reduce(
      (sum, group) => sum + group.seat_allocated,
      0
    );

    const totalSeats = totalBranchSeats + totalGroupSeats;

    if (totalSeats !== minMaxEnroll.max) {
      setModalMessage(
        `${t("Total allocated seats")} (${totalSeats}) ${t(
          "do not match the maximum allotment"
        )} (${minMaxEnroll.max}).`
      );
      setIsErrorModalOpen(true);
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (sessionStatus === "AVAILABLE") {
      setSessionBranches([]);
      setSessionGroups([]);
    }
  }, [sessionStatus]);

  const fetchDoceboSession = async (sessionId: number) => {
    await dispatch(
      getCourseSessionBranches({
        session_id: selectedSessionData.session_id,
        course_id: parseInt(selectedCourseData?.course_id),
        user_id: sessionStorage.getItem("userId"),
        role: "admin",
      })
    );
    await dispatch(
      getCourseSessionGroups({
        session_id: selectedSessionData.session_id,
        course_id: parseInt(selectedCourseData?.course_id),
        user_id: sessionStorage.getItem("userId"),
        role: "admin",
      })
    );
    await dispatch(getDoceboSession(sessionId));
  };

  const handleMinMaxSeatsSave = async () => {
    setUpdateLoading(true);
    try {
      if (minMaxEnroll.min > minMaxEnroll.max || minMaxEnroll.min < 0) {
        setUpdateLoading(false);
        setErrorMessage("Minimum seats cannot be greater than maximum seats");
        return;
        // throw new Error("Minimum seats cannot be greater than maximum seats");
      }
      setErrorMessage(null);
      const res = await dispatch(
        updateSessionById({
          max_enrollments: minMaxEnroll.max,
          min_enrollments: minMaxEnroll.min,
          session_id: selectedSessionData.session_id,
          course_id: parseInt(selectedCourseData?.course_id),
          user_id: sessionStorage.getItem("userId"),
          role: "admin",
        })
      );

      if (res.payload) {
        dispatch(setSelectedCourseSessionData(res.payload.data));
        setSessionStatus(res.payload.data.session_status);
        setMinMaxEnroll({
          min: res.payload.data.min_enroll,
          max: res.payload.data.max_enroll,
        });
      }

      setUpdateLoading(false);
    } catch (error) {
      console.log("error", error);
      setUpdateLoading(false);
    }
  };

  const updateAllocationData = (
    selectedType: string,
    id: number,
    value: number
  ) => {
    if (selectedType === "branch") {
      setSessionBranches((prev) =>
        prev.map((branch) =>
          branch.branch_id === id
            ? { ...branch, seat_allocated: value }
            : branch
        )
      );
    } else if (selectedType === "group") {
      setSessionGroups((prev) =>
        prev.map((group) =>
          group.group_id === id ? { ...group, seat_allocated: value } : group
        )
      );
    }
  };

  const getStateBySelectType = (selectedType: string) =>
    selectedType === "branch" ? filteredBranches : filteredGroups;

  const renderSeatTitle = (seat: SessionBranchItem | SessionGroupItem) =>
    "branch_title" in seat ? seat.branch_title : seat.group_name;

  const handleMinChange = (value: number) => {
    // if (value >= 0 && value <= minMaxEnroll.max) {
    setMinMaxEnroll((prev) => ({ ...prev, min: value }));
    // }
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const closeSaveModal = () => {
    setIsSaveModalOpen(false);
  };

  const handleMaxChange = (value: number) => {
    // if (value >= minMaxEnroll.min) {
    setMinMaxEnroll((prev) => ({ ...prev, max: value }));
    // }
  };

  const handleSave = () => {
    if (validateSeatAllocation() && sessionStatus === "ALLOCATION") {
      saveUpdatedSeatAllocation();
    } else {
      setIsSaveModalOpen(false);
    }
  };



  const saveUpdatedSeatAllocation = async () => {
    let bodyPayload = {
      min_enrollments: minMaxEnroll.min,
      max_enrollments: minMaxEnroll.max,
      session_name: selectedSessionData.session_name,
      seat_allocation_branches: sessionBranches.map((branch) => ({
        branch_id: branch.branch_id,
        seat_allocated: branch.seat_allocated,
      })),
      seat_allocation_groups: sessionGroups.map((group) => ({
        group_id: group.group_id,
        seat_allocated: group.seat_allocated,
      })),
    };

    await dispatch(
      updateSessionSeatAllocation({
        session_id: selectedSessionData.session_id,
        payload: bodyPayload,
        course_id: parseInt(selectedCourseData?.course_id),
        user_id: sessionStorage.getItem("userId"),
        role: "admin",
      })
    );

    setIsSaveModalOpen(false);
  };

  useEffect(() => {
    if (selectedSessionData) {
      setSessionStatus(selectedSessionData.session_status);
      setMinMaxEnroll({
        min: selectedSessionData.min_enroll,
        max: selectedSessionData.max_enroll,
      });
    }
  }, [selectedSessionData]);

  useEffect(() => {
    if (branches.length > 0) {
      if (sessionStatus !== "ALLOCATION" && sessionStatus !== "AVAILABLE") {
        setSessionBranches(
          branches.filter((branch) => 
            branch.seat_allocated !== null && branch.seat_allocated > 0
          )
        );
      } else setSessionBranches(branches);
    }

    if (groups.length > 0) {
      if (sessionStatus !== "ALLOCATION" && sessionStatus !== "AVAILABLE") {
        setSessionGroups(groups.filter((group) => 
          group.seat_allocated !== null && group.seat_allocated > 0
        ));
      } else setSessionGroups(groups);
    }
  }, [branches, groups, sessionStatus]);

  useEffect(() => {
    if (isEditableStatus(selectedSessionData.session_status)) {
      fetchDoceboSession(selectedSessionData.session_id);
    }
  }, [dispatch, selectedSessionData]);

  const data = getStateBySelectType(selectedType);

  const isMobileOrTablet = window.innerWidth < 1024; // Checking for mobile/tablet screens

  return (
    <div className="flex flex-col items-start gap-8 w-full">
      {isMobileOrTablet ? (
        <>
          <TabNavbar
            id="seat-allocation"
            tabs={ADMIN_TABS}
            selectedTabId={1}
            type="Seat Allocation"
            handleSaveAndPublish={() =>
              sessionStatus === "ALLOCATION" && setIsSaveModalOpen(true)
            }
            isLoading={saveLoading}
            isDisable={sessionStatus !== "ALLOCATION"}
            doesShow={sessionStatus !== "AVAILABLE"}
          />
          <BackButton />
        </>
      ) : (
        <>
          <BackButton />
          <TabNavbar
            id="seat-allocation"
            tabs={ADMIN_TABS}
            selectedTabId={1}
            type="Seat Allocation"
            handleSaveAndPublish={() =>
              sessionStatus === "ALLOCATION" && setIsSaveModalOpen(true)
            }
            isLoading={saveLoading}
            isDisable={sessionStatus !== "ALLOCATION"}
            doesShow={sessionStatus !== "AVAILABLE"}
          />
        </>
      )}
      <div className="flex flex-row w-full justify-between items-center group cursor-pointer">
        <div className="flex flex-col w-full">
          <p className="text-2xl font-semibold text-[#961e38]">
            {selectedCourseData?.course_name}
          </p>
        </div>
      </div>
      {Object.keys(selectedSessionData).length > 0 && (
        <div className="flex flex-row w-full justify-between items-center group cursor-pointer">
          <div className="flex flex-col w-full">
            <p className="text-2xl font-semibold text-[#961e38]">
              {selectedSessionData.session_name}
            </p>
          </div>
          {!isMobileOrTablet && <LanguageSelector />}
        </div>
      )}

      <div className="block md:hidden w-full h-full overflow-x-auto">
        <div className="px-3 py-2 flex sm:flex-row items-center sm:items-center gap-2 w-full">
          <GoSearch size={18} />
          <input
            type="text"
            placeholder={t("Search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={branchLoading && groupLoading}
            className="w-full p-2 border-b-2 border-white bg-inherit focus:border-white focus:outline-none"
          />
        </div>
        <div
          className="p-2 bg-[#961e38] font-semibold my-2"
          style={{ color: "white" }}
        >
          {selectedType === "branch" ? t("Branch") : t("Group")}
        </div>

        <div className="min-w-full">
          <div className="text-sm md:text-base">
            {branchLoading || groupLoading || loading ? (
              <div className="flex items-center w-full justify-center">
                <SpinnerLoader />
              </div>
            ) : data.length > 0 ? (
              data.map((seat: any, i) => (
                <div
                  key={i}
                  className="border border-[#c6c6c6] py-2 text-left bg-[#f3f3f3] mt-2"
                >
                  <div className="py-2 px-2 md:px-4 text-left">
                    {renderSeatTitle(seat)}
                  </div>
                  <div className="flex items-center justify-between w-full py-2 px-2">
                    <p> {t("Number of Seats")}</p>
                    <input
                      className="w-[50px] border border-[#909090] text-center"
                      value={seat.seat_allocated}
                      disabled={
                        sessionStatus === "APPLICATION" ||
                        sessionStatus === "EXCLUDED_AT_ALLOC"
                      }
                      onChange={(e) =>
                        updateAllocationData(
                          selectedType,
                          seat.branch_id || seat.group_id,
                          Number(e.target.value)
                        )
                      }
                    />
                  </div>
                </div>
              ))
            ) : (
              <p className="w-full text-center py-5">
                {/* {t("No Data Available, please add min and max seats")} */}
                {sessionStatus !== "ALLOCATION" &&
                sessionStatus !== "AVAILABLE" &&
                selectedType === "branch"
                  ? t("No seats assigned for branches")
                  : sessionStatus !== "ALLOCATION" &&
                    sessionStatus !== "AVAILABLE" &&
                    selectedType === "group"
                  ? t("No seats assigned for groups")
                  : t("No Data Available, please add min and max seats")}
              </p>
            )}
          </div>
          <div className="text-sm md:text-base">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
              <div className="flex justify-between w-full items-center gap-2 pt-2">
                <button
                  onClick={() =>
                    selectedType === "branch"
                      ? setSelectedType("group")
                      : setSelectedType("branch")
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "8px",
                    fontSize: "16px",
                    cursor: "pointer",
                  }}
                >
                  <IoMdSwap size={24} />
                </button>
                {sessionStatus !== "AVAILABLE" && (
                  <div className="flex items-center gap-5 w-full justify-end">
                    <button
                      disabled={sessionStatus !== "ALLOCATION" || saveLoading}
                      onClick={() =>
                        sessionStatus === "ALLOCATION" &&
                        setIsSaveModalOpen(true)
                      }
                      className={`py-2 px-4 font-semibold bg-[#961e38] text-white ${
                        sessionStatus !== "ALLOCATION"
                          ? "cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    >
                      {saveLoading
                        ? `${t("Saving")}...`
                        : sessionStatus !== "ALLOCATION"
                        ? t("Training Published")
                        : t("Save & Publish Training")}
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex flex-col items-center gap-2">
                  <div className="flex item-center gap-3">
                    <p className="font-semibold">{t("Min Seats")}</p>
                    <div className="relative group">
                      <input
                        className="w-12 text-center bg-white text-black"
                        value={minMaxEnroll.min}
                        onChange={(e) =>
                          handleMinChange(Number(e.target.value))
                        }
                        disabled={sessionStatus !== "AVAILABLE"}
                      />
                      {sessionStatus !== "AVAILABLE" && (
                        <span className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs p-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-max z-10">
                          {t(
                            "The seat min and max were selected and saved, and cannot be further edited"
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex item-center gap-3">
                    <p className="font-semibold">{t("Max Seats")}</p>
                    <div className="relative group">
                      <input
                        className="w-12 text-center bg-white text-black"
                        value={minMaxEnroll.max}
                        onChange={(e) =>
                          handleMaxChange(Number(e.target.value))
                        }
                        disabled={sessionStatus !== "AVAILABLE"}
                      />
                      {sessionStatus !== "AVAILABLE" && (
                        <span className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs p-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-max z-10">
                          {t(
                            "The seat min and max were selected and saved, and cannot be further edited"
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {sessionStatus === "AVAILABLE" && (
                  <button
                    className="py-1.5 px-5 font-semibold bg-[#961e38] text-white"
                    onClick={handleMinMaxSeatsSave}
                    disabled={updateLoading}
                  >
                    {updateLoading ? `${t("Saving")}...` : t("Save")}
                  </button>
                )}
              </div>
            </div>
            {errorMessage && (
              <p className="mt-3 text-red-300 text-sm">{errorMessage}</p>
            )}
          </div>
        </div>

        {/* Modals */}
        {isErrorModalOpen && (
          <ErrorModal
            closeModal={closeErrorModal}
            modalMessage={modalMessage}
          />
        )}
        {isSaveModalOpen && (
          <SaveConfirmModal
            closeModal={closeSaveModal}
            handleSave={handleSave}
            loading={saveLoading}
            msg="Are you sure you want to save current seat allocation for this session?"
          />
        )}
      </div>

      <div className="hidden md:block w-full h-full">
        <table className="w-full border-collapse">
          <thead className="bg-[#961e38] w-full text-white rounded-t-xl">
            <tr>
              <th colSpan={2} className="py-2 text-left rounded-t-xl">
                <div className="px-3 py-2 flex items-center gap-2 w-full">
                  <GoSearch size={18} />
                  <input
                    type="text"
                    placeholder={t("Search")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={branchLoading && groupLoading}
                    className="w-full p-2 border-b-2 border-white bg-inherit focus:border-white focus:outline-none"
                  />
                </div>
              </th>
            </tr>
            <tr>
              <th className="py-2 px-4 text-left">
                <div className="flex items-center gap-3">
                  <button
                    className={`py-1.5 px-10 cursor-pointer font-semibold ${
                      selectedType === "branch"
                        ? selectedTypeClassName
                        : "bg-[#961e38] text-white"
                    }`}
                    disabled={branchLoading && groupLoading}
                    onClick={() => setSelectedType("branch")}
                  >
                    {t("Branch")}
                  </button>
                  <button
                    className={`py-1.5 px-10 cursor-pointer font-semibold ${
                      selectedType === "group"
                        ? selectedTypeClassName
                        : "bg-[#961e38] text-white"
                    }`}
                    disabled={branchLoading && groupLoading}
                    onClick={() => setSelectedType("group")}
                  >
                    {t("Group")}
                  </button>
                </div>
              </th>
              <th className="py-2 px-4 text-right">{t("Number of Seats")}</th>
            </tr>
          </thead>
          <tbody>
            {branchLoading || groupLoading || loading ? (
              <tr>
                <td
                  colSpan={2}
                  className="flex items-center justify-center py-5"
                >
                  <SpinnerLoader />
                </td>
              </tr>
            ) : (
              <>
                {data.length > 0 ? (
                  data.map((seat: any, i) => (
                    <tr key={i}>
                      <td className="border-b border-[#c6c6c6] py-2 px-4 text-left">
                        {renderSeatTitle(seat)}
                      </td>
                      <td className="border-b border-[#c6c6c6] py-2 px-4 text-right">
                        <input
                          className="w-[50px] border border-[#909090] text-center"
                          value={seat.seat_allocated}
                          disabled={
                            sessionStatus === "APPLICATION" ||
                            sessionStatus === "EXCLUDED_AT_ALLOC"
                          }
                          onChange={(e) =>
                            updateAllocationData(
                              selectedType,
                              seat.branch_id || seat.group_id,
                              Number(e.target.value)
                            )
                          }
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2}>
                      <p className="w-full text-center py-5">
                        {/* {t("No Data Available, please add min and max seats")} */}
                        {sessionStatus !== "ALLOCATION" &&
                        sessionStatus !== "AVAILABLE" &&
                        selectedType === "branch"
                          ? t("No seats assigned for branches")
                          : sessionStatus !== "ALLOCATION" &&
                            sessionStatus !== "AVAILABLE" &&
                            selectedType === "group"
                          ? t("No seats assigned for groups")
                          : t(
                              "No Data Available, please add min and max seats"
                            )}
                      </p>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
          <tfoot className="bg-[#961e38]">
            <tr>
              <td colSpan={2} className="p-5 text-white rounded-b-xl">
                <div className="flex items-center justify-between rounded-b-xl">
                  <div className="flex items-center gap-2">
                    <p className="text-sm">{t("Min Seats")}</p>
                    <div className="relative group">
                      <input
                        className="w-12 text-center bg-white text-black"
                        value={minMaxEnroll.min}
                        onChange={(e) =>
                          handleMinChange(Number(e.target.value))
                        }
                        disabled={sessionStatus !== "AVAILABLE"}
                      />{" "}
                      {sessionStatus !== "AVAILABLE" && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs p-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-max">
                          {t(
                            "The seat min and max were selected and saved, and cannot be further edited"
                          )}
                        </span>
                      )}
                    </div>
                    <p className="text-sm">{t("Max Seats")}</p>
                    <div className="relative group">
                      <input
                        className="w-12 text-center bg-white text-black"
                        value={minMaxEnroll.max}
                        onChange={(e) =>
                          handleMaxChange(Number(e.target.value))
                        }
                        disabled={sessionStatus !== "AVAILABLE"}
                      />
                      {sessionStatus !== "AVAILABLE" && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs p-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-max">
                          {t(
                            "The seat min and max were selected and saved, and cannot be further edited"
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  {sessionStatus === "AVAILABLE" && (
                    <button
                      className="py-1.5 px-5 border border-white"
                      onClick={handleMinMaxSeatsSave}
                      disabled={updateLoading}
                    >
                      {updateLoading ? `${t("Saving")}...` : t("Save")}
                    </button>
                  )}
                </div>
                {errorMessage && (
                  <div>
                    <p>{errorMessage}</p>
                  </div>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
        {isErrorModalOpen && (
          <ErrorModal
            closeModal={closeErrorModal}
            modalMessage={modalMessage}
          />
        )}
        {isSaveModalOpen && (
          <SaveConfirmModal
            closeModal={closeSaveModal}
            handleSave={handleSave}
            loading={saveLoading}
            msg="Are you sure you want to save current seat allocation for this session?"
          />
        )}
      </div>
    </div>
  );
}

export default SeatAllocation;
